import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { Shimmer } from "@/components/Shimmer";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import { fmtSeconds, fmtNum, fmtCost } from "@/lib/format";

export default function OperationsPage() {
  const [hours, setHours] = useState("24");
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get("/operations", { params: { hours } }).then((r) => setData(r.data));
  }, [hours]);

  const hourly = (data?.hourly || []).map((h) => ({
    hour: h.hour.slice(5, 16), total: h.total, errored: h.errored,
  }));

  const sinceIso = (() => {
    const d = new Date(Date.now() - parseInt(hours || "24", 10) * 3600 * 1000);
    return d.toISOString();
  })();

  const nodeLatencyCols = useMemo(() => [
    { accessorKey: "node", header: "Node", cell: ({ getValue }) => <span className="capitalize strong">{getValue()}</span> },
    { accessorKey: "p50", header: "p50", meta: { align: "right" }, cell: ({ getValue }) => <span className="num">{fmtSeconds(getValue())}</span> },
    { accessorKey: "p95", header: "p95", meta: { align: "right" }, cell: ({ getValue }) => <span className="num">{fmtSeconds(getValue())}</span> },
    { accessorKey: "p99", header: "p99", meta: { align: "right" }, cell: ({ getValue }) => <span className="num">{fmtSeconds(getValue())}</span> },
    { accessorKey: "count", header: "n", meta: { align: "right" }, cell: ({ getValue }) => <span className="num meta">{getValue()}</span> },
  ], []);

  const llmByNodeCols = useMemo(() => [
    { accessorKey: "node", header: "Node", cell: ({ getValue }) => <span className="capitalize strong">{getValue()}</span> },
    { accessorKey: "invocations", header: "Calls", meta: { align: "right" }, cell: ({ getValue }) => <span className="num meta">{getValue()}</span> },
    { accessorKey: "tokens", header: "Tokens", meta: { align: "right" }, cell: ({ getValue }) => <span className="num meta">{fmtNum(getValue())}</span> },
    { accessorKey: "cost_usd", header: "Cost", meta: { align: "right" }, cell: ({ getValue }) => <span className="num strong">{fmtCost(getValue())}</span> },
    {
      accessorKey: "p50_ms", header: "p50 / p95 ms", meta: { align: "right" }, enableSorting: false,
      cell: ({ row }) => (
        <span className="num meta">
          {row.original.p50_ms ? `${Math.round(row.original.p50_ms)} / ${Math.round(row.original.p95_ms)}` : "—"}
        </span>
      ),
    },
  ], []);

  const llmByModelCols = useMemo(() => [
    { accessorKey: "model", header: "Model", cell: ({ getValue }) => <span className="mono" style={{ fontSize: 12 }}>{getValue()}</span> },
    { accessorKey: "invocations", header: "Calls", meta: { align: "right" }, cell: ({ getValue }) => <span className="num meta">{getValue()}</span> },
    { accessorKey: "input_tokens", header: "Input", meta: { align: "right" }, cell: ({ getValue }) => <span className="num meta">{fmtNum(getValue())}</span> },
    { accessorKey: "output_tokens", header: "Output", meta: { align: "right" }, cell: ({ getValue }) => <span className="num meta">{fmtNum(getValue())}</span> },
    { accessorKey: "cost_usd", header: "Cost", meta: { align: "right" }, cell: ({ getValue }) => <span className="num strong">{fmtCost(getValue())}</span> },
  ], []);

  return (
    <div className="p-8 max-w-[1600px]" data-testid="operations-page">
      <PageHeader
        eyebrow="SRE"
        title="Operations"
        subtitle="Agent health, error signatures, and per-node latency."
        actions={
          <Select value={hours} onValueChange={setHours}>
            <SelectTrigger className="w-[140px]" data-testid="ops-window-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Last hour</SelectItem>
              <SelectItem value="24">Last 24h</SelectItem>
              <SelectItem value="168">Last 7d</SelectItem>
              <SelectItem value="720">Last 30d</SelectItem>
            </SelectContent>
          </Select>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {data ? (
          <>
            <Stat label="Total emails" value={fmtNum(data.totals.total)}     to={`/emails?date_from=${sinceIso}`} />
            <Stat label="Processed"    value={fmtNum(data.totals.processed)} to={`/emails?status=processed&date_from=${sinceIso}`} />
            <Stat label="Errored"      value={fmtNum(data.totals.errored)}   to={`/emails?status=error&date_from=${sinceIso}`} highlight={data.totals.errored > 0} />
            <Stat label="Error rate"   value={`${data.error_rate_pct}%`}     highlight={data.error_rate_pct > 5} />
          </>
        ) : [...Array(4)].map((_, i) => <Shimmer key={i} className="h-24" />)}
      </div>

      {/* LLM cost row */}
      {data?.llm && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6" data-testid="ops-llm">
          <Stat label="LLM spend"         value={fmtCost(data.llm.total_cost_usd)} />
          <Stat label="LLM calls"         value={fmtNum(data.llm.invocations)} />
          <Stat label="Input tokens"      value={fmtNum(data.llm.input_tokens)} />
          <Stat label="Output tokens"     value={fmtNum(data.llm.output_tokens)} />
          <Stat label="Avg call duration" value={`${(data.llm.avg_duration_ms / 1000).toFixed(2)}s`} />
        </div>
      )}

      <section className="card-surface p-4 mb-6" data-testid="hourly-chart">
        <div className="overline mb-3">Emails per hour · total vs errored</div>
        <div className="h-[260px]">
          {data && hourly.length ? (
            <ResponsiveContainer>
              <LineChart data={hourly}>
                <CartesianGrid stroke="#F4F4F5" vertical={false} />
                <XAxis dataKey="hour" tick={{ fontSize: 11, fontFamily: "Geist Mono" }} />
                <YAxis tick={{ fontSize: 11, fontFamily: "Geist Mono" }} />
                <Tooltip contentStyle={{ fontSize: 13, fontFamily: "Geist Mono" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="total" stroke="#09090B" strokeWidth={1.5} dot={false} />
                <Line type="monotone" dataKey="errored" stroke="#F43F5E" strokeWidth={1.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full grid place-items-center text-sm meta">No data in window</div>
          )}
        </div>
      </section>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Top error signatures — drill-down */}
        <section className="card-surface" data-testid="top-errors">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <div className="overline">Top error signatures</div>
            <Link to={`/emails?status=error&date_from=${sinceIso}`} className="text-xs mono text-zinc-600 hover:text-zinc-900">view all errored →</Link>
          </div>
          <ul className="divide-y divide-border">
            {data?.top_error_signatures?.length ? data.top_error_signatures.map((r, i) => (
              <li key={i} className="px-4 py-3 flex items-baseline justify-between gap-3">
                <Link
                  to={`/emails?status=error&q=${encodeURIComponent((r.signature || "").slice(0, 40))}&date_from=${sinceIso}`}
                  className="mono truncate flex-1 text-zinc-700 hover:text-zinc-900 hover:underline"
                  style={{ fontSize: 13 }}
                >
                  {r.signature}
                </Link>
                <span className="mono text-xs text-rose-700">× {r.count}</span>
              </li>
            )) : <li className="px-4 py-6 text-center text-sm meta">No errors</li>}
          </ul>
        </section>

        {/* Node latency */}
        <DataTable
          data-testid="node-latency"
          columns={nodeLatencyCols}
          data={data?.node_latency || []}
          emptyMessage="No completed events"
          maxHeight="none"
          header={<div className="overline">Node latency · from email ingestion</div>}
        />

        {/* LLM by node */}
        <DataTable
          data-testid="llm-by-node"
          columns={llmByNodeCols}
          data={data?.llm?.by_node || []}
          emptyMessage="No LLM calls"
          maxHeight="none"
          header={<div className="overline">LLM cost · by node</div>}
        />

        {/* LLM by model */}
        <DataTable
          data-testid="llm-by-model"
          columns={llmByModelCols}
          data={data?.llm?.by_model || []}
          emptyMessage="No LLM calls"
          maxHeight="none"
          header={<div className="overline">LLM cost · by model</div>}
        />
      </div>
    </div>
  );
}

function Stat({ label, value, highlight, to }) {
  const body = (
    <>
      <div className="overline">{label}</div>
      <div className={`font-mono text-3xl mt-1 ${highlight ? "text-rose-700" : "text-zinc-900"}`}>{value}</div>
      {to && <div className="text-[10px] meta mono mt-1 opacity-0 group-hover:opacity-100 transition-opacity">view →</div>}
    </>
  );
  if (to) {
    return <Link to={to} className="card-surface p-4 block hover:shadow-sm transition-shadow group">{body}</Link>;
  }
  return <div className="card-surface p-4">{body}</div>;
}
